/**
 * Hook for creating research on a contact or company.
 *
 * Submits three events via the CRM events endpoint:
 *   1. `ahoy_object_created` — the research entity
 *   2. `ahoy_research_to_<contact|company>` — links to the target
 *   3. `ahoy_research_to_user` — links to the requester, required for
 *      scoped-permission read access. Without this, users with the `User`
 *      role cannot see the research they just requested, because
 *      `filter_accessible_crm_objects` filters it out via the scoping rules
 *      in `apps/orgs/entities/permission_groups.yaml` (`ahoy_research`).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';

interface UseCreateResearchOptions {
  /** Organization ID */
  orgId: number;
  /** Entity ID of the contact or company */
  entityId: string;
  /** Record type: 'contact' or 'company' */
  recordType: 'contact' | 'company';
}

interface UseCreateResearchResult {
  /** Trigger research creation */
  createResearch: () => Promise<string | null>;
  /** Whether research creation is in progress */
  isCreating: boolean;
  /** Error message if creation failed */
  errorMessage: string | null;
}

/**
 * Hook to create a new research object and associate it with a contact or company.
 *
 * @example
 * const { createResearch, isCreating } = useCreateResearch({
 *   orgId: 1,
 *   entityId: 'abc-123',
 *   recordType: 'contact'
 * });
 *
 * const handleClick = async () => {
 *   const researchId = await createResearch();
 *   if (researchId) {
 *     console.log('Created research:', researchId);
 *   }
 * };
 */
export function useCreateResearch({
  orgId,
  entityId,
  recordType,
}: UseCreateResearchOptions): UseCreateResearchResult {
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();
  const { entityId: userEntityId } = useCurrentUserEntity();

  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createResearch = useCallback(async (): Promise<string | null> => {
    if (!orgId || !user?.id || !entityId || !userEntityId) {
      setErrorMessage('Missing required information');
      return null;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const researchEntityId = crypto.randomUUID();

      const targetAssociationTypeName =
        recordType === 'contact'
          ? 'ahoy_research_to_contact'
          : 'ahoy_research_to_company';

      await submitEventsOptimistic({
        orgId,
        userId: user.id,
        entityTypeName: 'ahoy_research',
        primaryEntityId: researchEntityId,
        errorToast: false,
        build: b => {
          b.objectCreated(researchEntityId, 'ahoy_research', {
            ahoy_research_type: 'manual',
          });
          b.associationAdded(
            researchEntityId,
            'ahoy_research',
            targetAssociationTypeName,
            entityId
          );
          b.associationAdded(
            researchEntityId,
            'ahoy_research',
            'ahoy_research_to_user',
            userEntityId
          );
        },
      });

      queryClient.invalidateQueries({
        queryKey: ['research', orgId, entityId, recordType],
      });

      toast.success('Research started');
      return researchEntityId;
    } catch (error) {
      logger.error(
        'useCreateResearch: failed to create research',
        error instanceof Error ? error : new Error(String(error))
      );
      const err = error as {
        statusCode?: number;
        response?: { error?: string; detail?: string };
      };
      const msg =
        err.response?.error ||
        err.response?.detail ||
        "We couldn't start the research. Try again.";
      setErrorMessage(msg);
      toast.error('Research failed', { description: msg });
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [orgId, user?.id, userEntityId, entityId, recordType, queryClient]);

  return {
    createResearch,
    isCreating,
    errorMessage,
  };
}
